import { NARRATIVE_TITLE_MAX_CHARS } from '@/pages/Narrative/utils/validation';
import { Button, cn, tv } from '@simplywallst/ui-core/next';
import AiSolid from '@simplywallst/ui-core/icons/AiSolid';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import useResizeObserver from 'use-resize-observer';
import { useTracking } from 'react-tracking';
import { useNarrativeTitleSuggestions } from '@/pages/Narrative/hooks/useNarrativeTitleSuggestions';
import type { NarrativeForm } from '@/pages/Narrative/utils/schema';
import { useSnackbar } from '@/features/snackbar';

const common = tv({
  base: [
    'w-full',
    'border-solid',
    'border-2',
    'border-transparent',
    'transition-[opacity,border-color,color,background-color]',
    'bg-transparent',
    'whitespace-pre-wrap',
    'break-words',
    '[word-break:break-word]',
    'overflow-hidden',
    'text-soft',
    'font-strong',
    'font-sans',
    'resize-none',
    'rounded-sm',
    'text-mega',
    'font-strong',
    'col-start-1',
    'row-start-1',
    'p-x0_25',
  ],
});

const commonClasses = common();

const title = tv({
  slots: {
    editor: [
      commonClasses,
      'focus:text-solid',
      'focus:bg-surface-2',
      '[.s-focus-kb_&]:focus-within:outline-dotted [.s-focus-kb_&]:focus-within:outline-2 [.s-focus-kb_&]:focus-within:outline-offset-2 [.s-focus-kb_&]:focus-within:outline-[--s-focus-color]',
      'disabled:cursor-not-allowed',
      'disabled:text-softer',
    ],
    mask: [commonClasses, 'invisible'],
    errorMessage: 'text-tiny text-bad',
  },
  variants: {
    hasError: {
      true: {
        editor: 'border-bad',
      },
    },
    isEmpty: {
      true: {
        editor: 'pr-[100px]',
        mask: 'pr-[100px]',
      },
    },
  },
  defaultVariants: {
    hasError: false,
    isEmpty: true,
  },
});

type Props = {
  values: NarrativeForm;
  onChange: (values: Partial<NarrativeForm>) => void;
  error: string | null | undefined;
  companyId: string;
};

const PLACEHOLDER = "What's your take on this stock";

export const NarrativeTitle = ({
  values,
  onChange,
  error,
  companyId,
}: Props) => {
  const [originalTitle, setOriginalTitle] = useState<null | string>(null);
  const { trackEvent } = useTracking({ subject: 'title' });
  const { addSnackbar } = useSnackbar();
  const id = useId();

  const value = values.title;
  const { editor, mask, errorMessage } = title({
    hasError: !!error,
    isEmpty: !value,
  });
  const endOfLineRef = useRef<HTMLSpanElement>(null);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [position, setPosition] = useState({
    top: 0,
    left: 0,
    isButtonOverflowing: false,
  });
  const [isFocused, setIsFocused] = useState(false);
  const { width } = useResizeObserver({ ref: textAreaRef });
  const { width: buttonWidth, ref: buttonWrapperRef } = useResizeObserver();
  const getCoordinates = useCallback(() => {
    const textarea = textAreaRef.current;
    const span = endOfLineRef.current;
    if (!textarea || !span)
      return { top: 0, left: 0, isButtonOverflowing: false };

    const spanRect = span.getBoundingClientRect();
    const textareaRect = textarea.getBoundingClientRect();

    const left = spanRect.left - textareaRect.left + textarea.scrollLeft;
    const buttonWidthAdjusted = buttonWidth ?? 0;

    return {
      top: spanRect.top - textareaRect.top + textarea.scrollTop,
      left: Math.min(textareaRect.width - buttonWidthAdjusted, left),
      isButtonOverflowing: left + buttonWidthAdjusted > textareaRect.width,
    };
  }, [buttonWidth]);

  const { isLoading: isGenerating, nextSuggestion } =
    useNarrativeTitleSuggestions({
      companyId,
      contentHtml: values.content.html,
      title: values.title,
      fairValue: values.fairValue,
      discountRate: values.discountRatePercentage,
      futureSharesOutstanding: values.futureSharesOutstanding,
      futurePePercentageChange: values.priceEarningsMultiple,
      profitMarginPercentageChange: values.profitMarginPercentage,
      revenuePercentageChange: values.revenuePercentage,
      timeframe: values.timePeriod,
      sharesOutstandingGrowthRate: values.sharesOutstandingGrowthRate,
    });

  const handleOnGenerate = useCallback(async () => {
    try {
      const title = await nextSuggestion();
      trackEvent({ action: 'generate', old: value, new: title });
      !originalTitle && setOriginalTitle(value || null);
      onChange({ title });
    } catch (err) {
      console.error('Failed to generate AI title', err);
      addSnackbar({
        message: 'Failed to generate a title',
        type: 'negative',
        lifeTime: 2000,
      });
    }
  }, [onChange, nextSuggestion, addSnackbar, originalTitle, value, trackEvent]);

  useEffect(() => {
    setPosition(getCoordinates());
  }, [value, width, buttonWidth, getCoordinates]);

  return (
    <div className="group/title relative">
      <label className="sr-only" htmlFor={id}>
        Narrative title
      </label>
      <div className="grid">
        <div className={mask()}>
          {value || PLACEHOLDER}
          <span ref={endOfLineRef} />
        </div>
        <textarea
          id={id}
          ref={textAreaRef}
          className={editor()}
          onChange={(e) =>
            onChange({ title: e.target.value.replace(/(\r\n|\n|\r)/gm, '') })
          }
          rows={1}
          value={value}
          maxLength={NARRATIVE_TITLE_MAX_CHARS}
          placeholder={PLACEHOLDER}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          autoComplete="off"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={isGenerating}
        />
      </div>
      <div
        className={cn(
          'absolute grid grid-cols-[max-content_max-content]',
          'left-0 top-0 opacity-0 transition-opacity focus-within:opacity-100 group-hover/title:opacity-100',
          Boolean(
            isGenerating ||
              (isFocused && !position.isButtonOverflowing) ||
              !value
          ) && 'opacity-100'
        )}
        style={{
          transform: `translate(${position.left}px, ${position.top}px)`,
        }}
        ref={buttonWrapperRef}
      >
        <div
          className="w-x2 bg-gradient-to-r from-transparent to-p-grey-50"
          onClick={() => {
            const textArea = textAreaRef.current;
            if (!textArea) return;

            textArea.focus();
            const textLength = textArea.value.length;
            textArea.setSelectionRange(textLength, textLength);
          }}
        />
        <div
          className={cn(
            'grid gap-x1 rounded-md bg-surface-1',
            isFocused && 'bg-surface-2',
            originalTitle
              ? 'grid-cols-[max-content_max-content]'
              : 'grid-cols-[max-content]'
          )}
        >
          <Button
            variant="outline"
            size="medium"
            onClick={handleOnGenerate}
            isLoading={isGenerating}
          >
            <AiSolid />
            {isGenerating ? 'Generating..' : value ? 'Rewrite' : 'Generate'}
          </Button>
          {originalTitle && (
            <Button
              variant="outline"
              size="medium"
              onClick={() => {
                onChange({ title: originalTitle });
                setOriginalTitle(null);
              }}
            >
              Undo
            </Button>
          )}
        </div>
      </div>
      {error && <div className={errorMessage()}>{error}</div>}
    </div>
  );
};
